// Canevas du schéma fonctionnel (SVG maison) : déplacement à la souris ou au doigt, alternative clavier complète,
// liaisons typées (couleur + étiquette ; pointillés = à vérifier), tracé d'une liaison depuis la poignée, zoom et déplacement de la vue.
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { COURT_LIAISON, LIBELLE_LIAISON, LIBELLE_NOEUD, compatibiliteEffective, type Donnees, type Liaison, type Noeud } from './modele.ts';

export const L_NOEUD = 184;
export const H_NOEUD = 68;
const PAS = 8;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 2.5;

export type Selection = { type: 'noeud' | 'liaison'; id: string } | null;
export interface Vue { x: number; y: number; k: number }
export interface CanevasPoignee { zoomer: (facteur: number) => void; ajuster: () => void; centre: () => { x: number; y: number }; reinitialiser: () => void }

interface Props {
  donnees: Donnees;
  selection: Selection;
  choisir: (s: Selection) => void;
  deplacer: (id: string, x: number, y: number, geste: string) => void;
  relier: (de: string, vers: string) => void;
  editer: () => void;
  supprimer: (s: NonNullable<Selection>) => void;
  lectureSeule: boolean;
  surZoom?: (k: number) => void;
}

const aligner = (v: number) => Math.round(v / PAS) * PAS;
const tronquer = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** Géométrie d'une liaison : courbe décalée quand plusieurs liaisons relient la même paire. */
function geometrie(l: Liaison, a: Noeud, b: Noeud, rang: number, total: number) {
  const x1 = a.x + L_NOEUD / 2; const y1 = a.y + H_NOEUD / 2;
  const x2 = b.x + L_NOEUD / 2; const y2 = b.y + H_NOEUD / 2;
  const dx = x2 - x1; const dy = y2 - y1;
  const lg = Math.hypot(dx, dy) || 1;
  // Normale orientée de façon stable (indépendante du sens de la liaison).
  const sens = a.id < b.id ? 1 : -1;
  const decal = (rang - (total - 1) / 2) * 30 * sens;
  const cx = (x1 + x2) / 2 - (dy / lg) * decal;
  const cy = (y1 + y2) / 2 + (dx / lg) * decal;
  return { d: `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`, mx: 0.25 * x1 + 0.5 * cx + 0.25 * x2, my: 0.25 * y1 + 0.5 * cy + 0.25 * y2 };
}

export const CanevasSchema = forwardRef<CanevasPoignee, Props>(function CanevasSchema(
  { donnees, selection, choisir, deplacer, relier, editer, supprimer, lectureSeule, surZoom }, ref) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [taille, setTaille] = useState({ l: 800, h: 520 });
  const [vue, setVue] = useState<Vue>({ x: -40, y: -40, k: 1 });
  const geste = useRef<null | { mode: 'noeud' | 'vue' | 'lien'; id?: string; px: number; py: number; ox: number; oy: number; bouge: boolean; cle: string }>(null);
  const [lienEnCours, setLienEnCours] = useState<{ de: string; x: number; y: number } | null>(null);

  useEffect(() => { surZoom?.(vue.k); }, [vue.k, surZoom]);

  // Taille réelle du canevas (pour le viewBox).
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setTaille({ l: Math.max(200, e.contentRect.width), h: Math.max(200, e.contentRect.height) }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const versMonde = useCallback((cx: number, cy: number) => {
    const r = svgRef.current!.getBoundingClientRect();
    return { x: vue.x + (cx - r.left) / vue.k, y: vue.y + (cy - r.top) / vue.k };
  }, [vue]);

  const zoomerAutour = useCallback((facteur: number, cx?: number, cy?: number) => {
    setVue((v) => {
      const k = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v.k * facteur));
      const px = cx ?? taille.l / 2; const py = cy ?? taille.h / 2;
      const mx = v.x + px / v.k; const my = v.y + py / v.k;
      return { k, x: mx - px / k, y: my - py / k };
    });
  }, [taille]);

  const ajuster = useCallback((kMax = ZOOM_MAX) => {
    if (!donnees.nodes.length) { setVue({ x: -40, y: -40, k: 1 }); return; }
    const xs = donnees.nodes.map((n) => n.x); const ys = donnees.nodes.map((n) => n.y);
    const minX = Math.min(...xs) - 40; const minY = Math.min(...ys) - 40;
    const maxX = Math.max(...xs) + L_NOEUD + 40; const maxY = Math.max(...ys) + H_NOEUD + 40;
    const k = Math.min(kMax, ZOOM_MAX, Math.max(ZOOM_MIN, Math.min(taille.l / (maxX - minX), taille.h / (maxY - minY))));
    setVue({ k, x: minX - (taille.l / k - (maxX - minX)) / 2, y: minY - (taille.h / k - (maxY - minY)) / 2 });
  }, [donnees.nodes, taille]);

  // Premier affichage : tout le schéma visible, sans agrandir au-delà de 100 %.
  const cadreInitial = useRef(false);
  useEffect(() => {
    if (cadreInitial.current || !svgRef.current) return;
    const r = svgRef.current.getBoundingClientRect();
    if (Math.abs(r.width - taille.l) > 1) return; // taille réelle pas encore mesurée
    cadreInitial.current = true;
    ajuster(1);
  }, [taille, ajuster]);

  useImperativeHandle(ref, () => ({
    zoomer: (f) => zoomerAutour(f),
    ajuster: () => ajuster(),
    centre: () => ({ x: aligner(vue.x + taille.l / vue.k / 2 - L_NOEUD / 2), y: aligner(vue.y + taille.h / vue.k / 2 - H_NOEUD / 2) }),
    reinitialiser: () => setVue({ x: -40, y: -40, k: 1 }),
  }), [zoomerAutour, ajuster, vue, taille]);

  // Molette : zoom autour du pointeur (écouteur non passif pour empêcher le défilement de la page).
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const roue = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoomerAutour(e.deltaY < 0 ? 1.12 : 1 / 1.12, e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener('wheel', roue, { passive: false });
    return () => el.removeEventListener('wheel', roue);
  }, [zoomerAutour]);

  // ─── Pointeur ───
  const debutNoeud = (e: PointerEvent, n: Noeud) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    choisir({ type: 'noeud', id: n.id });
    if (lectureSeule) return;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    geste.current = { mode: 'noeud', id: n.id, px: e.clientX, py: e.clientY, ox: n.x, oy: n.y, bouge: false, cle: `glisser-${n.id}-${Date.now()}` };
  };
  const debutVue = (e: PointerEvent) => {
    if (e.button !== 0) return;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    geste.current = { mode: 'vue', px: e.clientX, py: e.clientY, ox: vue.x, oy: vue.y, bouge: false, cle: '' };
  };
  const debutLien = (e: PointerEvent, n: Noeud) => {
    e.stopPropagation();
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    const p = versMonde(e.clientX, e.clientY);
    geste.current = { mode: 'lien', id: n.id, px: e.clientX, py: e.clientY, ox: 0, oy: 0, bouge: false, cle: '' };
    setLienEnCours({ de: n.id, x: p.x, y: p.y });
  };
  const mouvement = (e: PointerEvent) => {
    const g = geste.current;
    if (!g) return;
    const dx = e.clientX - g.px; const dy = e.clientY - g.py;
    if (!g.bouge && Math.hypot(dx, dy) < 4) return;
    g.bouge = true;
    if (g.mode === 'noeud') deplacer(g.id!, aligner(g.ox + dx / vue.k), aligner(g.oy + dy / vue.k), g.cle);
    else if (g.mode === 'vue') setVue((v) => ({ ...v, x: g.ox - dx / v.k, y: g.oy - dy / v.k }));
    else { const p = versMonde(e.clientX, e.clientY); setLienEnCours((l) => (l ? { ...l, x: p.x, y: p.y } : l)); }
  };
  const fin = (e: PointerEvent) => {
    const g = geste.current;
    geste.current = null;
    if (!g) return;
    if (g.mode === 'vue' && !g.bouge) choisir(null);
    if (g.mode === 'lien') {
      setLienEnCours(null);
      const cible = (document.elementFromPoint(e.clientX, e.clientY) as Element | null)?.closest('[data-noeud]')?.getAttribute('data-noeud');
      if (cible && cible !== g.id) relier(g.id!, cible);
    }
  };

  // ─── Clavier ───
  const toucheNoeud = (e: KeyboardEvent, n: Noeud) => {
    const pas = e.shiftKey ? PAS * 5 : PAS;
    const fleches: Record<string, [number, number]> = { ArrowLeft: [-pas, 0], ArrowRight: [pas, 0], ArrowUp: [0, -pas], ArrowDown: [0, pas] };
    if (fleches[e.key] && !lectureSeule) {
      e.preventDefault();
      const [dx, dy] = fleches[e.key];
      deplacer(n.id, n.x + dx, n.y + dy, `clavier-${n.id}`);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault(); editer();
    } else if ((e.key === 'Delete' || e.key === 'Backspace') && !lectureSeule) {
      e.preventDefault(); supprimer({ type: 'noeud', id: n.id });
    }
  };
  const toucheLiaison = (e: KeyboardEvent, l: Liaison) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); editer(); }
    else if ((e.key === 'Delete' || e.key === 'Backspace') && !lectureSeule) { e.preventDefault(); supprimer({ type: 'liaison', id: l.id }); }
  };
  const toucheFond = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.target !== e.currentTarget) return;
    const pas = 40 / vue.k;
    const fleches: Record<string, [number, number]> = { ArrowLeft: [-pas, 0], ArrowRight: [pas, 0], ArrowUp: [0, -pas], ArrowDown: [0, pas] };
    if (fleches[e.key]) { e.preventDefault(); setVue((v) => ({ ...v, x: v.x + fleches[e.key][0], y: v.y + fleches[e.key][1] })); }
    if (e.key === '+' || e.key === '=') zoomerAutour(1.2);
    if (e.key === '-') zoomerAutour(1 / 1.2);
  };

  // ─── Rendu ───
  const parId = new Map(donnees.nodes.map((n) => [n.id, n]));
  const paires = new Map<string, Liaison[]>();
  for (const l of donnees.links) {
    const k = [l.de, l.vers].sort().join('|');
    paires.set(k, [...(paires.get(k) ?? []), l]);
  }
  const largeurVue = taille.l / vue.k; const hauteurVue = taille.h / vue.k;
  const r = 1 / vue.k;

  return (
    <svg ref={svgRef} className="sch-canevas" viewBox={`${vue.x} ${vue.y} ${largeurVue} ${hauteurVue}`} role="application" tabIndex={0}
      aria-label="Schéma fonctionnel. Tab pour parcourir les équipements et les liaisons ; flèches pour déplacer la vue ou l’équipement choisi ; plus et moins pour zoomer."
      aria-roledescription="éditeur de schéma" onPointerMove={mouvement} onPointerUp={fin} onPointerCancel={fin} onKeyDown={toucheFond}>
      <defs>
        <pattern id="sch-grille-fine" width={16} height={16} patternUnits="userSpaceOnUse">
          <path d="M 16 0 L 0 0 0 16" className="sch-grille-trait" strokeWidth={r} />
        </pattern>
        <pattern id="sch-grille" width={80} height={80} patternUnits="userSpaceOnUse">
          <rect width={80} height={80} fill="url(#sch-grille-fine)" />
          <path d="M 80 0 L 0 0 0 80" className="sch-grille-trait sch-grille-forte" strokeWidth={r * 1.2} />
        </pattern>
      </defs>
      <rect x={vue.x} y={vue.y} width={largeurVue} height={hauteurVue} fill="url(#sch-grille)" className="sch-fond" onPointerDown={debutVue} />

      <g aria-label="Liaisons">
        {donnees.links.map((l) => {
          const a = parId.get(l.de); const b = parId.get(l.vers);
          if (!a || !b) return null;
          const groupe = paires.get([l.de, l.vers].sort().join('|'))!;
          const g = geometrie(l, a, b, groupe.indexOf(l), groupe.length);
          const eff = compatibiliteEffective(l, donnees.nodes);
          const choisie = selection?.type === 'liaison' && selection.id === l.id;
          const court = COURT_LIAISON[l.type];
          const lg = court.length * 7 + 14;
          return (
            <g key={l.id} className={`sch-liaison sch-l-${l.type} ${eff.etat === 'a_verifier' ? 'est-a-verifier' : ''} ${choisie ? 'est-choisi' : ''}`}
              tabIndex={0} role="button" aria-pressed={choisie}
              aria-label={`Liaison ${LIBELLE_LIAISON[l.type]} de « ${a.label} » vers « ${b.label} », ${eff.etat === 'etayee' ? 'compatibilité étayée' : 'compatibilité à vérifier'}${l.libelle ? `, ${l.libelle}` : ''}`}
              onFocus={() => choisir({ type: 'liaison', id: l.id })} onKeyDown={(e) => toucheLiaison(e, l)}
              onPointerDown={(e) => { e.stopPropagation(); choisir({ type: 'liaison', id: l.id }); }}
              onDoubleClick={editer}>
              <path d={g.d} className="sch-liaison-zone" />
              {choisie && <path d={g.d} className="sch-liaison-halo" />}
              <path d={g.d} className="sch-liaison-trait" />
              <g transform={`translate(${g.mx} ${g.my})`} className="sch-liaison-etiquette">
                <rect x={-lg / 2} y={-10} width={lg} height={20} rx={4} />
                <text y={4} textAnchor="middle">{court}</text>
              </g>
            </g>
          );
        })}
        {lienEnCours && parId.get(lienEnCours.de) && (
          <line className="sch-lien-en-cours" x1={parId.get(lienEnCours.de)!.x + L_NOEUD} y1={parId.get(lienEnCours.de)!.y + H_NOEUD / 2}
            x2={lienEnCours.x} y2={lienEnCours.y} />
        )}
      </g>

      <g aria-label="Équipements">
        {donnees.nodes.map((n) => {
          const choisi = selection?.type === 'noeud' && selection.id === n.id;
          return (
            <g key={n.id} data-noeud={n.id} transform={`translate(${n.x} ${n.y})`} tabIndex={0} role="button" aria-pressed={choisi}
              className={`sch-noeud sch-n-${n.type} ${choisi ? 'est-choisi' : ''}`}
              aria-label={`${LIBELLE_NOEUD[n.type]} « ${n.label} »${n.reference ? `, référence ${n.reference}` : ''}${n.protocoles.length ? `, protocoles ${n.protocoles.join(', ')}` : ', aucun protocole déclaré'}`}
              onFocus={() => choisir({ type: 'noeud', id: n.id })} onKeyDown={(e) => toucheNoeud(e, n)}
              onPointerDown={(e) => debutNoeud(e, n)} onDoubleClick={editer}>
              <rect className="sch-noeud-cadre" width={L_NOEUD} height={H_NOEUD} rx={6} />
              <rect className="sch-noeud-repere" width={4} height={H_NOEUD - 12} x={0} y={6} rx={2} />
              <text className="sch-noeud-type" x={12} y={18}>{LIBELLE_NOEUD[n.type]}{n.zone ? ` · ${tronquer(n.zone, 14)}` : ''}</text>
              <text className="sch-noeud-nom" x={12} y={38}>{tronquer(n.label, 22)}</text>
              <text className="sch-noeud-proto" x={12} y={56}>{n.protocoles.length ? tronquer(n.protocoles.join(' · '), 26) : 'aucun protocole déclaré'}</text>
              {choisi && !lectureSeule && (
                <g className="sch-poignee" transform={`translate(${L_NOEUD} ${H_NOEUD / 2})`} onPointerDown={(e) => debutLien(e, n)}
                  aria-hidden="true">
                  <circle r={22} className="sch-poignee-zone" />
                  <circle r={7} className="sch-poignee-point" />
                </g>
              )}
            </g>
          );
        })}
      </g>
    </svg>
  );
});
